import React, { useRef, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { buscarMunicipios } from '../../../../services/nfeService';
import { MunicipioItem } from '../../../../types/nfe';

function useDebounce(value: string, delay = 350): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

interface MunicipioTypeaheadProps {
  value: number | null;            // municipioId selecionado
  displayValue?: string;           // texto a exibir (nome - UF)
  onChange: (municipio: MunicipioItem) => void;
  disabled?: boolean;
  isInvalid?: boolean;
}

const MunicipioTypeahead: React.FC<MunicipioTypeaheadProps> = ({
  value,
  displayValue,
  onChange,
  disabled,
  isInvalid,
}) => {
  const [inputText, setInputText] = useState(displayValue ?? '');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const debouncedTermo = useDebounce(inputText);

  // Sync external displayValue changes (e.g. form reset)
  useEffect(() => {
    setInputText(displayValue ?? '');
  }, [displayValue]);

  const query = useQuery({
    queryKey: ['municipios', debouncedTermo],
    queryFn: () => buscarMunicipios(debouncedTermo),
    enabled: open && debouncedTermo.length >= 2,
    staleTime: 60_000,
  });

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (item: MunicipioItem) => {
    setInputText(`${item.nome} - ${item.uf}`);
    setOpen(false);
    onChange(item);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        type="text"
        className={`form-control form-control-sm${isInvalid ? ' is-invalid' : ''}`}
        value={inputText}
        onChange={(e) => {
          setInputText(e.target.value);
          setOpen(true);
        }}
        onFocus={() => { if (inputText.length >= 2) setOpen(true); }}
        placeholder="Buscar município…"
        disabled={disabled}
        autoComplete="off"
      />
      {open && (query.data?.length ?? 0) > 0 && (
        <div
          className="border rounded bg-body shadow-sm"
          style={{
            position: 'absolute',
            zIndex: 1050,
            width: '100%',
            maxHeight: 220,
            overflowY: 'auto',
            top: '100%',
            left: 0,
          }}
        >
          {query.data!.map((item) => (
            <button
              key={item.id}
              type="button"
              className="d-flex align-items-center gap-2 w-100 text-start px-3 py-2 border-0 bg-transparent"
              style={{ fontSize: '0.82rem', cursor: 'pointer' }}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(item); }}
            >
              <span className="flex-grow-1">{item.nome}</span>
              <span className="badge bg-secondary-subtle text-secondary-emphasis" style={{ fontSize: '0.6rem' }}>
                {item.uf}
              </span>
            </button>
          ))}
        </div>
      )}
      {open && query.isLoading && (
        <div className="border rounded bg-body shadow-sm px-3 py-2" style={{ position: 'absolute', zIndex: 1050, width: '100%', top: '100%', fontSize: '0.8rem', color: 'var(--bs-secondary)' }}>
          Buscando…
        </div>
      )}
    </div>
  );
};

export default MunicipioTypeahead;
